// Everything currently believed about one project, from every tagger.
//
// The LATEST answer per (tagger, subject, tag), not one run's view: an operator
// looking at a project is asking "what do we think about this", and "what did run
// 41 say" is a different question with a different page.
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@bi/shared";

import { EmptyState, ErrorState, LoadingState } from "../components/states";
import { projectTagsQuery } from "../lib/queries";

export function ProjectTagsPage({ projectId }: { projectId: string }) {
  const tags = useQuery(projectTagsQuery(projectId));

  if (tags.isPending) return <LoadingState label="Loading tags…" />;
  if (tags.isError) return <ErrorState error={tags.error} onRetry={() => void tags.refetch()} />;
  if (tags.data.length === 0) {
    return (
      <EmptyState
        title="No tags on this project"
        message="No tagging run has covered it yet, or nothing any tagger asks applied to it."
      />
    );
  }

  // Grouped by tagger, because "what does the outreach tagger think" and "what
  // does the brief tagger think" are different questions and interleaving them
  // makes both harder to read.
  const byTagger = new Map<string, typeof tags.data>();
  for (const t of tags.data) {
    const list = byTagger.get(t.tagger) ?? [];
    list.push(t);
    byTagger.set(t.tagger, list);
  }

  return (
    <div className="bi-page">
      <div className="bi-page__head">
        <h1 className="bi-page__title">Project tags</h1>
        <p className="bi-page__sub">
          {tags.data.length} tag{tags.data.length === 1 ? "" : "s"} across {byTagger.size} tagger
          {byTagger.size === 1 ? "" : "s"} · {projectId}
        </p>
      </div>

      {[...byTagger.entries()].map(([tagger, rows]) => (
        <div className="bi-panel" key={tagger}>
          <div className="bi-panel__head">
            <h2 className="bi-panel__title">{tagger}</h2>
            <Badge size="sm" tone="neutral" variant="soft">
              {rows.length}
            </Badge>
          </div>
          <div className="bi-table-wrap">
            <table className="bi-table">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Answer</th>
                  <th>Evidence</th>
                  <th>Subject</th>
                  <th>Tagged</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={`${t.subjectRef}:${t.tagId}`}>
                    <td>{t.tagName}</td>
                    <td>
                      <Badge size="sm" tone="accent" variant="soft">
                        {t.answer ?? "-"}
                      </Badge>
                    </td>
                    {/* The quote is the whole reason a finding is trustworthy: a
                        verdict with no evidence is an assertion. */}
                    <td className="bi-table__prose">
                      {t.evidenceQuote ? `“${t.evidenceQuote}”` : "-"}
                    </td>
                    <td>{t.subjectRef === projectId ? "the project" : t.subjectRef}</td>
                    <td>{t.taggedAt ? t.taggedAt.slice(0, 16).replace("T", " ") : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
